import React, { useEffect, useState } from 'react';
import { Modal, Button, Input, Label, Badge, Spinner, Switch } from '@/components/ui';
import { Coins } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import {
  getOrgTokens,
  adjustOrgTokens,
  setOwnDeviceRate,
  issueWireInvoice,
  type OrgTokenInfo,
} from '@/services/tokenAdminService';
import { setOrgOverage } from '@/services/billingService';

interface Props {
  orgId: string;
  orgName: string;
  /** Current post-paid overage state for the org (passed by the caller). */
  allowOverage?: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

/** Super-admin token panel: balance, manual grant/deduct, own-device rate, overage, ledger. */
export function TokenManagementDialog({ orgId, orgName, allowOverage, onClose, onChanged }: Props) {
  const { success, error: toastError } = useToast();
  const [info, setInfo] = useState<OrgTokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [delta, setDelta] = useState('');
  const [note, setNote] = useState('');
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [overage, setOverage] = useState(!!allowOverage);
  const [overageBusy, setOverageBusy] = useState(false);
  // Wire-invoice form (also converts a trial → wire)
  const [invPlan, setInvPlan] = useState('');
  const [invAmount, setInvAmount] = useState('');
  const [invInterval, setInvInterval] = useState<'month' | 'year'>('month');
  const [invEmail, setInvEmail] = useState('');
  const [invDone, setInvDone] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getOrgTokens(orgId);
      setInfo(data);
      setRate(data.ownDeviceTokenRate == null ? '' : String(data.ownDeviceTokenRate));
      setInvPlan(data.plan || '');
    } catch (e: any) {
      toastError(e?.message ?? 'Failed to load token info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    setOverage(!!allowOverage);
  }, [allowOverage, orgId]);

  const applyOverage = async (next: boolean) => {
    setOverage(next);
    setOverageBusy(true);
    try {
      await setOrgOverage(orgId, next);
      success(next ? 'Post-paid overage enabled' : 'Post-paid overage disabled');
      onChanged?.();
    } catch (e: any) {
      setOverage(!next);
      toastError(e?.message ?? 'Failed to update overage');
    } finally {
      setOverageBusy(false);
    }
  };

  const applyAdjust = async () => {
    const n = Number(delta);
    if (!Number.isFinite(n) || n === 0) {
      toastError('Enter a non-zero amount (positive to grant, negative to deduct)');
      return;
    }
    setBusy(true);
    try {
      await adjustOrgTokens(orgId, n, note || undefined);
      success(`Balance adjusted by ${n > 0 ? '+' : ''}${n}`);
      setDelta('');
      setNote('');
      await load();
      onChanged?.();
    } catch (e: any) {
      toastError(e?.message ?? 'Adjust failed');
    } finally {
      setBusy(false);
    }
  };

  const applyRate = async () => {
    const value = rate.trim() === '' ? null : Number(rate);
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      toastError('Rate must be a non-negative number, or blank to inherit the plan default');
      return;
    }
    setBusy(true);
    try {
      await setOwnDeviceRate(orgId, value);
      success(value === null ? 'Own-device rate reset to plan default' : `Own-device rate set to ${value}`);
      await load();
      onChanged?.();
    } catch (e: any) {
      toastError(e?.message ?? 'Failed to set rate');
    } finally {
      setBusy(false);
    }
  };

  const applyIssueInvoice = async () => {
    if (!invPlan.trim()) { toastError('Enter the plan id to invoice (e.g. business or custom_<slug>)'); return; }
    const amt = invAmount.trim() === '' ? undefined : Number(invAmount);
    if (amt !== undefined && (!Number.isFinite(amt) || amt <= 0)) { toastError('Amount must be a positive number, or blank to use the plan price'); return; }
    setBusy(true);
    setInvDone(null);
    try {
      const r = await issueWireInvoice(orgId, {
        plan: invPlan.trim(),
        interval: invInterval,
        ...(amt !== undefined ? { amountUsd: amt } : {}),
        ...(invEmail.trim() ? { email: invEmail.trim() } : {}),
      });
      setInvDone(`Invoice ${r.reference} issued${r.emailedTo ? ` — emailed to ${r.emailedTo}` : ' (no email on file)'}. Org activates on payment.`);
      success('Wire invoice issued');
      onChanged?.();
    } catch (e: any) {
      toastError(e?.message ?? 'Failed to issue wire invoice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Tokens — ${orgName}`} description="Manage token balance, own-device rate, issue invoices, and view the ledger." size="lg">
      {loading || !info ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : (
        <div className="space-y-5">
          {/* Balance summary */}
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-4">
            <Coins className="w-5 h-5 text-muted-foreground" />
            <div>
              <div className="text-2xl font-semibold text-foreground leading-none">
                {info.tokenBalance.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                tokens · plan {info.plan ?? '—'}
              </div>
            </div>
          </div>

          {/* Adjust */}
          <div className="space-y-2">
            <Label htmlFor="tok-delta">Grant / deduct tokens</Label>
            <div className="flex items-center gap-2">
              <Input id="tok-delta" type="number" placeholder="±amount" value={delta}
                onChange={(e) => setDelta(e.target.value)} className="w-32" />
              <Input placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="flex-1" />
              <Button size="sm" disabled={busy} onClick={applyAdjust}>Apply</Button>
            </div>
            <p className="text-xs text-muted-foreground">Positive grants, negative deducts. Recorded as an audited adjustment.</p>
          </div>

          {/* Own-device rate */}
          <div className="space-y-2">
            <Label htmlFor="tok-rate">Own-device token rate (override)</Label>
            <div className="flex items-center gap-2">
              <Input id="tok-rate" type="number" step="0.1" min="0" placeholder="inherit plan" value={rate}
                onChange={(e) => setRate(e.target.value)} className="w-32" />
              <Button size="sm" variant="outline" disabled={busy} onClick={applyRate}>Save rate</Button>
            </div>
            <p className="text-xs text-muted-foreground">0 = own-device tests free, 1 = full charge. Blank inherits the plan default.</p>
          </div>

          {/* Allow overage (post-paid) */}
          <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
            <div>
              <Label className="text-sm">Allow overage (post-paid)</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Let this org run past its token balance and bill the overage at the end of the period.
              </p>
            </div>
            <Switch checked={overage} onCheckedChange={applyOverage} disabled={overageBusy} />
          </div>

          {/* Issue wire invoice (bank transfer via Wise; also converts a trial → wire) */}
          <div className="space-y-2 rounded-lg border border-border p-3">
            <Label>Issue wire invoice (bank transfer via Wise)</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input placeholder="plan id" value={invPlan} onChange={(e) => setInvPlan(e.target.value)} className="w-40" />
              <Input type="number" placeholder="amount $ (opt)" value={invAmount} onChange={(e) => setInvAmount(e.target.value)} className="w-36" />
              <select
                value={invInterval}
                onChange={(e) => setInvInterval(e.target.value as 'month' | 'year')}
                className="px-2 py-2 rounded-md border border-border bg-background text-foreground text-sm"
              >
                <option value="month">monthly</option>
                <option value="year">annual</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Input placeholder="billing email (defaults to org owner)" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} className="flex-1" />
              <Button size="sm" variant="outline" disabled={busy} onClick={applyIssueInvoice}>Issue wire invoice</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Sets the org to wire billing and emails an invoice with your Wise bank details + a unique reference.
              The org activates + gets tokens automatically when the transfer arrives. Amount blank = use the plan price;
              set an amount for a negotiated Enterprise deal.
            </p>
            {invDone && <p className="text-sm text-status-success">{invDone}</p>}
          </div>

          {/* Ledger */}
          <div>
            <Label>Recent ledger</Label>
            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border">
              {info.ledger.length === 0 ? (
                <p className="text-sm text-muted-foreground p-3">No ledger entries yet.</p>
              ) : (
                info.ledger.map((row) => (
                  <div key={row.id} className="flex items-center justify-between gap-3 p-2.5 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant={row.type === 'grant' ? 'success' : row.type === 'adjustment' ? 'default' : 'secondary'}>
                        {row.reason}
                      </Badge>
                      <span className="text-muted-foreground truncate">{row.note ?? row.allocationId ?? ''}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={row.delta < 0 ? 'text-status-error' : row.delta > 0 ? 'text-status-success' : 'text-muted-foreground'}>
                        {row.delta > 0 ? '+' : ''}{row.delta}
                      </span>
                      <span className="text-muted-foreground tabular-nums">{row.balanceAfter}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
